import React, { FC, useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import { Modal, Typography, Spin, Row, Col, message, Select, Button, Space, Image, Card, Tag, Popconfirm, Radio, Input } from "antd";
import { apiGetShippingItems, apiGetAlternativeCarriers, retryShipment, triggerShippingCron, saveSelfTracking, getSelfShippingBrands, checkShippingItems } from "services/ordersApi";
import { FailedShippingItem, AlternativeCarrier, GetOrderDetailData, MasterAllTypeItem, CheckShippingItemsItem } from "services/interface";
import { useGetOrderDetail } from "hooks/useOrders";
import { ReloadOutlined, CodeSandboxOutlined, RightOutlined, CarOutlined } from "@ant-design/icons";
import {
  SelfShippingBrand,
  isPhoneTrackingBrand,
  mapSelfShippingBrands,
  validateSelfTrackingNumber,
} from "./selfTracking";
import SelfTrackingForm from "./SelfTrackingForm";

interface ShippingResultModalProps {
  visible: boolean;
  orderId: string | null;
  onClose: () => void;
  refetchData: () => void;
}

const ShippingResultModal: FC<ShippingResultModalProps> = ({ visible, orderId, onClose, refetchData }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<FailedShippingItem[]>([]);
  const [orderDetail, setOrderDetail] = useState<GetOrderDetailData | null>(null);

  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [selectedSkuDesc, setSelectedSkuDesc] = useState<string | null>(null);
  const [selectedFailedItem, setSelectedFailedItem] = useState<FailedShippingItem | null>(null);

  const [alternativeCarriers, setAlternativeCarriers] = useState<Record<string, AlternativeCarrier[]>>({});
  const [carriersLoading, setCarriersLoading] = useState<Record<string, boolean>>({});
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);
  const [retryLoading, setRetryLoading] = useState<boolean>(false);
  const [cronLoading, setCronLoading] = useState<boolean>(false);

  // --- Self shipping tracking form ---
  const [transportBrands, setTransportBrands] = useState<SelfShippingBrand[]>([]);
  const inputRef = useRef<any>(null);
  const selectRef = useRef<any>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [selfBrandId, setSelfBrandId] = useState<string | undefined>(undefined);
  const [selfTrackingNumber, setSelfTrackingNumber] = useState<string>('');
  const [selfSaveLoading, setSelfSaveLoading] = useState(false);
  const [selfSubmitted, setSelfSubmitted] = useState(false);
  const [selfTrackingError, setSelfTrackingError] = useState<string | null>(null);
  /**
   * retail_order_item_id → วิธีส่ง/สถานะ (checkShippingItems)
   * ชิ้น "ส่งเอง" ที่ยังไม่ออก Label ไม่มีแถวใน shipping items → ใช้ดูว่ายังกรอกเลขพัสดุได้ไหม
   */
  const [selfItemsById, setSelfItemsById] = useState<Record<string, CheckShippingItemsItem>>({});

  const { mutate: fetchOrderDetail } = useGetOrderDetail();

  useEffect(() => {
    if (visible && orderId) {
      loadData(false);
      if (transportBrands.length === 0) {
        getSelfShippingBrands().then((res: any) => {
          setTransportBrands(mapSelfShippingBrands(res));
        }).catch(() => {});
      }
    } else {
      setItems([]);
      setOrderDetail(null);
      setSelectedProductName(null);
      setSelectedSkuDesc(null);
      setSelectedFailedItem(null);
      setSelfItemsById({});
    }
  }, [visible, orderId]);

  // ฟัง forceRefreshShippingModal event จาก WebSocket (เมื่อได้ tracking ใหม่)
  // โหลดข้อมูลใหม่แบบ silent (ไม่แสดง spinner) เฉพาะเมื่อ Modal เปิดอยู่
  useEffect(() => {
    const handleRefresh = () => {
      if (visible && orderId) {
        loadData(true);
      }
    };
    window.addEventListener('forceRefreshShippingModal', handleRefresh);
    return () => {
      window.removeEventListener('forceRefreshShippingModal', handleRefresh);
    };
  }, [visible, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async (silent = false): Promise<any[] | undefined> => {
    if (!orderId) return;
    if (!silent) setLoading(true);

    return new Promise((resolve) => {
      try {
        fetchOrderDetail(
          {
            key_type: "order_id",
            group_by: "factory",
            items: [orderId],
          },
          {
            onSuccess: async (data) => {
              const detail = data?.data?.[0];
              setOrderDetail(detail || null);

              // Fetch shipping statuses
              try {
                const [res] = await Promise.all([apiGetShippingItems({ order_id: orderId }), loadSelfItems()]);
                if (res.status === "success" && res.data) {
                  setItems(res.data);
                  resolve(res.data);  // return fresh items
                } else {
                  resolve(undefined);
                }
              } catch (e) {
                message.error("ไม่สามารถดึงข้อมูลสถานะการจัดส่งได้");
                resolve(undefined);
              } finally {
                if (!silent) setLoading(false);
              }
            },
            onError: () => {
              message.error("ไม่สามารถดึงข้อมูลคำสั่งซื้อได้");
              if (!silent) setLoading(false);
              resolve(undefined);
            },
          }
        );
      } catch (e) {
        if (!silent) setLoading(false);
        resolve(undefined);
      }
    });
  };

  const loadSelfItems = async () => {
    if (!orderId) return;
    try {
      const res = await checkShippingItems([orderId]);
      const list = res.data?.details?.find((d) => d.order_id === orderId)?.items || [];
      setSelfItemsById(Object.fromEntries(list.map((i) => [i.retail_order_item_id, i])));
    } catch (e) {
      // ไม่ได้ข้อมูล → ชิ้นที่ไม่มีแถวจัดส่งแสดง "ไม่มีข้อมูลขนส่ง" ตามเดิม
    }
  };

  const loadAlternativeCarriers = async (verify_id: string) => {
    setSelectedCarrier(null);
    if (alternativeCarriers[verify_id]) return;
    setCarriersLoading((prev) => ({ ...prev, [verify_id]: true }));
    try {
      const res = await apiGetAlternativeCarriers(verify_id);
      if (res.status === "success" && res.data) {
        setAlternativeCarriers((prev) => ({ ...prev, [verify_id]: res.data }));
      }
    } catch (e) {
      message.error("ไม่สามารถดึงตัวเลือกขนส่งใหม่ได้");
    } finally {
      setCarriersLoading((prev) => ({ ...prev, [verify_id]: false }));
    }
  };

  const handleRetry = async () => {
    if (!selectedFailedItem) return;
    if (retryLoading) return;
    if (!selectedCarrier) {
      return message.warning("กรุณาเลือกขนส่งใหม่");
    }
    setRetryLoading(true);
    try {
      const res = await retryShipment({
        verify_ids: [selectedFailedItem.verify_id],
        new_carrier_code: selectedCarrier,
      });
      if (res.status?.toLowerCase() === "success") {
        message.success("ส่งคำขอเปลี่ยนขนส่งสำเร็จ");
        const freshItems = await loadData(true);
        refetchData();
        if (!freshItems) {
          return message.warning("บันทึกข้อมูลสำเร็จ แต่ไม่สามารถโหลดข้อมูลล่าสุดได้ กรุณาปิดแล้วเปิดใหม่");
        }
        // navigate to next pending sub-item within same SKU using FRESH items
        const nextInSameSku = freshItems.find(
          (i) =>
            i.verify_id !== selectedFailedItem.verify_id &&
            i.product_name === selectedProductName &&
            i.sku_name === selectedSkuDesc &&
            (i.status?.toUpperCase() === 'FAILED' || (i.shipment_type === 'self' && !i.self_tracking_number))
        );
        setSelectedFailedItem(nextInSameSku || null);
        if (nextInSameSku && nextInSameSku.status?.toUpperCase() === 'FAILED' && nextInSameSku.shipment_type !== 'self') {
          loadAlternativeCarriers(nextInSameSku.verify_id);
        }
      } else {
        message.error("ไม่สามารถแก้ไขขนส่งได้");
      }
    } catch (e) {
      message.error("ไม่สามารถแก้ไขขนส่งได้");
    } finally {
      setRetryLoading(false);
    }
  };

  const handleSelfShip = async () => {
    if (!selectedFailedItem) return;
    if (retryLoading) return;
    setRetryLoading(true);
    try {
      const res = await retryShipment({
        verify_ids: [selectedFailedItem.verify_id],
        new_carrier_code: "self",
      });
      if (res.status?.toLowerCase() === "success") {
        message.success("เปลี่ยนเป็นส่งเองสำเร็จ");
        const freshItems = await loadData(true);
        refetchData();
        if (!freshItems) {
          return message.warning("บันทึกข้อมูลสำเร็จ แต่ไม่สามารถโหลดข้อมูลล่าสุดได้ กรุณาปิดแล้วเปิดใหม่");
        }
        // navigate to next pending sub-item within same SKU using FRESH items
        //   Priority 1: FAILED bangbow items (still need carrier action)
        //   Priority 2: Self items that still need tracking number
        const nextFailedBangbow = freshItems.find(
          (i) =>
            i.verify_id !== selectedFailedItem.verify_id &&
            i.product_name === selectedProductName &&
            i.sku_name === selectedSkuDesc &&
            i.status?.toUpperCase() === 'FAILED' &&
            i.shipment_type !== 'self'
        );
        const nextPendingSelf = freshItems.find(
          (i) =>
            i.verify_id !== selectedFailedItem.verify_id &&
            i.product_name === selectedProductName &&
            i.sku_name === selectedSkuDesc &&
            i.shipment_type === 'self' &&
            !i.self_tracking_number
        );
        const nextInSameSku = nextFailedBangbow || nextPendingSelf;
        setSelectedFailedItem(nextInSameSku || null);
        if (nextFailedBangbow) {
          loadAlternativeCarriers(nextFailedBangbow.verify_id);
        } else if (nextPendingSelf) {
          setTimeout(() => {
            selectRef.current?.focus();
            setDropdownOpen(true);
          }, 150);
        }
      } else {
        message.error("ทำรายการไม่สำเร็จ");
      }
    } catch (e) {
      message.error("ทำรายการไม่สำเร็จ");
    } finally {
      setRetryLoading(false);
    }
  };

  const handleSaveSelfTracking = async () => {
    if (!selectedFailedItem) return;
    if (selfSaveLoading) return;
    setSelfSubmitted(true);
    if (!selfBrandId) {
      return message.warning('กรุณาเลือกบริษัทขนส่ง');
    }
    const isSelectedFT = isPhoneTrackingBrand(transportBrands, selfBrandId);

    const trackingError = validateSelfTrackingNumber(selfTrackingNumber, isSelectedFT);
    setSelfTrackingError(trackingError);
    if (trackingError) return;
    setSelfSaveLoading(true);
    try {
      await saveSelfTracking({
        verify_id: selectedFailedItem.verify_id,
        self_transport_brand_id: selfBrandId,
        self_tracking_number: selfTrackingNumber.trim(),
      });
      message.success('บันทึกข้อมูลจัดส่งสำเร็จ');
      
      // Dispatch event ให้ TrackingNumberPanel อัปเดตข้อมูลทันที
      window.dispatchEvent(new CustomEvent('forceRefreshTrackingPanel'));
      
      // 1. Reload data first to get fresh state
      const freshItems = await loadData(true);
      refetchData();
      if (!freshItems) {
        return message.warning("บันทึกข้อมูลสำเร็จ แต่ไม่สามารถโหลดข้อมูลล่าสุดได้ กรุณาปิดแล้วเปิดใหม่");
      }

      // 2. Navigate to next pending item in same SKU using FRESH items
      //    Priority 1: FAILED bangbow items (still need carrier action)
      //    Priority 2: Self items that still need tracking number
      const nextFailedBangbow = freshItems.find(
        (i) =>
          i.verify_id !== selectedFailedItem.verify_id &&
          i.product_name === selectedProductName &&
          i.sku_name === selectedSkuDesc &&
          i.status?.toUpperCase() === 'FAILED' &&
          i.shipment_type !== 'self'
      );
      const nextPendingSelf = freshItems.find(
        (i) =>
          i.verify_id !== selectedFailedItem.verify_id &&
          i.product_name === selectedProductName &&
          i.sku_name === selectedSkuDesc &&
          i.shipment_type === 'self' &&
          !i.self_tracking_number
      );
      const nextInSameSku = nextFailedBangbow || nextPendingSelf;
      setSelectedFailedItem(nextInSameSku || null);

      if (nextInSameSku) {
        if (nextFailedBangbow) {
          // ถ้ารายการถัดไปเป็น FAILED bangbow ให้โหลดตัวเลือกขนส่งทันที
          loadAlternativeCarriers(nextFailedBangbow.verify_id);
        } else {
          // ถ้ารายการถัดไปเป็นส่งเอง ให้ auto-open dropdown เลือกบริษัทขนส่ง
          setTimeout(() => {
            selectRef.current?.focus();
            setDropdownOpen(true);
          }, 150);
        }
      }
      // selectedProductName / selectedSkuDesc stay unchanged — remain on same SKU

      setSelfTrackingNumber('');
      setSelfBrandId(undefined);
      setSelfSubmitted(false);
    } catch (e) {
      message.error('บันทึกข้อมูลไม่สำเร็จ');
    } finally {
      setSelfSaveLoading(false);
    }
  };

  const handleTriggerCron = async () => {
    setCronLoading(true);
    try {
      await triggerShippingCron();
      message.success("เรียก Cron สำเร็จ กำลังโหลดข้อมูลใหม่...");
      await loadData(true);
      refetchData();
    } catch (e) {
      message.error("เรียก Cron ไม่สำเร็จ");
    } finally {
      setCronLoading(false);
    }
  };

  // ชิ้นส่งเองที่ไม่มีแถวจัดส่ง: ยัง factory_approve = รอกรอกเลข · สถานะถัดไป = บันทึกเลขแล้ว
  const getSelfItemStatus = (order_item_id?: string) => {
    const s = order_item_id ? selfItemsById[order_item_id] : undefined;
    if (!s || s.shipping_method !== "self" || s.item_status === "cancel") return "NONE";
    return s.item_status === "factory_approve" ? "SELF_NO_TRACKING" : "SELF_SAVED";
  };

  const getSkuStatus = (product_name: string, sku_desc: string | null, order_item_id?: string) => {
    const shipItems = items.filter((i) => i.product_name === product_name && i.sku_name === sku_desc);
    if (shipItems.length === 0) return getSelfItemStatus(order_item_id);
    if (shipItems.some((i) => i.status?.toUpperCase() === "FAILED" && !i.carrier_code)) return "NO_CARRIER";
    if (shipItems.some((i) => i.status?.toUpperCase() === "FAILED")) return "FAILED";
    if (shipItems.some((i) => i.shipment_type === "self" && !i.self_tracking_number)) return "SELF_NO_TRACKING";
    if (shipItems.some((i) => i.status?.toUpperCase() === "PENDING" || i.status?.toUpperCase() === "PROCESSING")) return "PENDING";
    return "SUCCESS";
  };

  const sortedOrderItems = [...(orderDetail?.items || [])].sort((a, b) => {
    const statusA = getSkuStatus(a.product_name, a.sku_description, a.order_item_id);
    const statusB = getSkuStatus(b.product_name, b.sku_description, b.order_item_id);
    
    const priority: Record<string, number> = { "NO_CARRIER": 1, "FAILED": 1, "SELF_NO_TRACKING": 2, "PENDING": 3, "SUCCESS": 4, "SELF_SAVED": 4, "NONE": 5 };
    const pA = priority[statusA] || 99;
    const pB = priority[statusB] || 99;
    
    return pA - pB;
  });

  const getSortedSkuShipItems = (product_name: string | null, sku_description: string | null) =>
    items
      .filter((i) => i.product_name === product_name && i.sku_name === sku_description)
      .sort((a, b) => {
        const getSingleStatus = (i: typeof items[0]) => {
          if (i.status?.toUpperCase() === "FAILED" && !i.carrier_code) return "NO_CARRIER";
          if (i.status?.toUpperCase() === "FAILED") return "FAILED";
          if (i.shipment_type === "self" && !i.self_tracking_number) return "SELF_NO_TRACKING";
          if (i.status?.toUpperCase() === "PENDING" || i.status?.toUpperCase() === "PROCESSING") return "PENDING";
          return "SUCCESS";
        };

        const statA = getSingleStatus(a);
        const statB = getSingleStatus(b);

        const priority: Record<string, number> = { "NO_CARRIER": 1, "FAILED": 1, "SELF_NO_TRACKING": 2, "PENDING": 3, "SUCCESS": 4, "NONE": 5 };
        return (priority[statA] || 99) - (priority[statB] || 99);
      });

  const selectedSkuShipItems = getSortedSkuShipItems(selectedProductName, selectedSkuDesc);

  // SKU ที่เลือกเป็นชิ้นส่งเองที่ยังไม่มีแถวจัดส่ง → สถานะ + ฟอร์มกรอกเลขพัสดุ
  const selectedOrderItem =
    selectedSkuShipItems.length === 0
      ? sortedOrderItems.find((i) => i.product_name === selectedProductName && i.sku_description === selectedSkuDesc)
      : undefined;
  const selectedSelfStatus = getSelfItemStatus(selectedOrderItem?.order_item_id);

  const handleSelfItemSaved = async () => {
    await loadData(true);
    refetchData();
  };

  const handleSelectSku = (product_name: string, sku_description: string | null) => {
    setSelectedProductName(product_name);
    setSelectedSkuDesc(sku_description);
    setSelfSubmitted(false);
    const firstItem = getSortedSkuShipItems(product_name, sku_description)[0] ?? null;
    setSelectedFailedItem(firstItem);
    if (firstItem) {
      loadAlternativeCarriers(firstItem.verify_id);
    }
  };

  useEffect(() => {
    if (visible && !loading && !selectedProductName && sortedOrderItems.length > 0) {
      handleSelectSku(sortedOrderItems[0].product_name, sortedOrderItems[0].sku_description);
    }
  }, [visible, loading, selectedProductName, sortedOrderItems]);

  const handleChangeCarrierClick = (e: React.MouseEvent, product_name: string, sku_description: string | null) => {
    e.stopPropagation();
    handleSelectSku(product_name, sku_description);
    const firstFailed = items.find((i) => i.product_name === product_name && i.sku_name === sku_description && i.status?.toUpperCase() === "FAILED");
    if (firstFailed) {
      setSelectedFailedItem(firstFailed);
      loadAlternativeCarriers(firstFailed.verify_id);
    }
  };

  const handleActionClick = (item: FailedShippingItem) => {
    setSelectedFailedItem(item);
    setSelfSubmitted(false);
    loadAlternativeCarriers(item.verify_id);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          {/* สำหรับทดสอบเท่านั้น ถ้าขึ้น Production ต้องเอาออก */}
          {/*
          <Button onClick={handleTriggerCron} type="default" loading={cronLoading}>
            เรียกขนส่งใหม่ (ทดสอบ)
          </Button>*/}
          <Button onClick={onClose} type="primary">
            ปิดหน้าจอ
          </Button>
        </Space>
      }
      title={
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          รายละเอียดการเรียกขนส่ง เลขที่คำสั่งซื้อ :{orderDetail?.items[0]?.order_code ? ` ${orderDetail.items[0].order_code}` : ""}
        </Typography.Title>
      }
      destroyOnClose
      width={1200}
      maskClosable={!selfSaveLoading && !retryLoading && !cronLoading && !loading}
      closable={!selfSaveLoading && !retryLoading && !cronLoading && !loading}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", height: "60vh" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={16}>
          {/* ขวา ซ้ายสุด: รายการ (Master) */}
          <Col xs={24} md={24} lg={24} xl={8}>
            <HeaderTab>
              <Typography.Text style={{ fontWeight: "normal", paddingBottom: 5, paddingTop: 5 }}>
                รายการสินค้า
              </Typography.Text>
            </HeaderTab>
            <div style={{ borderRight: "1px solid #f0f0f0", borderLeft: "1px solid #f0f0f0", height: "65vh", overflowY: "auto", padding: "8px" }}>
              {sortedOrderItems.map((item, index) => {
                const status = getSkuStatus(item.product_name, item.sku_description, item.order_item_id);
                const isSelected = selectedSkuDesc === item.sku_description && selectedProductName === item.product_name;

                return (
                  <div
                    key={`${index}-${item.product_name}-${item.sku_description}`}
                    onClick={() => handleSelectSku(item.product_name, item.sku_description)}
                    style={{
                      border: isSelected ? "1px solid #1890ff" : "1px solid #f0f0f0",
                      backgroundColor: isSelected ? "#e6f7ff" : "#fff",
                      padding: 10,
                      marginBottom: 8,
                      cursor: "pointer",
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                      <div style={{ flex: 1.5 }}>
                        <img
                          src={item.image_url || "/placeholder-image.png"}
                          alt={item.product_name}
                          style={{
                            width: 80,
                            height: 80,
                            marginRight: 10,
                            objectFit: "cover",
                            borderRadius: 4
                          }}
                        />
                      </div>
                      <div style={{ flex: 3, marginLeft: 10 }}>
                        <Typography.Paragraph style={{ marginBottom: 0, fontWeight: 500, fontSize: 14, lineHeight: 1.2 }}>
                          {item.product_name}
                        </Typography.Paragraph>
                        <Typography.Paragraph style={{ fontSize: "14px", color: "#888", marginBottom: 0 }}>
                          {item.sku_description}
                        </Typography.Paragraph>
                        <div style={{ marginTop: 8 }}>
                          {status === "FAILED" && <Tag color="red" style={{ fontSize: 14 }}>❌ ขนส่งปฏิเสธ</Tag>}
                          {status === "NO_CARRIER" && <Tag color="orange" style={{ whiteSpace: 'normal', height: 'auto', padding: '2px 6px', lineHeight: 1.4, fontSize: 14 }}>⚠️ SKU นี้ไม่ได้ตั้งค่าขนส่ง</Tag>}
                          {status === "SELF_NO_TRACKING" && <Tag color="orange" style={{ whiteSpace: 'normal', height: 'auto', padding: '2px 6px', lineHeight: 1.4, fontSize: 14 }}>⚠️ ยังไม่ได้บันทึกเลขพัสดุ</Tag>}
                          {status === "PENDING" && <Tag color="blue" style={{ fontSize: 14 }}>⏳ กำลังเรียกขนส่ง</Tag>}
                          {status === "SUCCESS" && <Tag color="green" style={{ fontSize: 14 }}>✅ เรียกขนส่งสำเร็จ</Tag>}
                          {status === "SELF_SAVED" && <Tag color="green" style={{ fontSize: 14 }}>✅ บันทึกเลขพัสดุแล้ว</Tag>}
                          {status === "NONE" && <Tag color="default" style={{ fontSize: 14 }}>ไม่มีข้อมูลขนส่ง</Tag>}
                        </div>
                      </div>
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "end", justifyContent: "space-between" }}>
                        <Typography.Paragraph style={{ marginBottom: 0, fontSize: 14 }}>
                          จำนวน {item.sku_cnt}
                        </Typography.Paragraph>
                        {(status === "FAILED" || status === "NO_CARRIER") && (
                          <Button
                            size="small"
                            style={{
                              fontSize: 14,
                              marginTop: 'auto',
                              backgroundColor: '#faad14', 
                              borderColor: '#d48806', 
                              color: '#000', 
                              fontWeight: 'normal',
                              borderWidth: 1,
                              borderStyle: 'solid'
                            }}
                            onClick={(e) => handleChangeCarrierClick(e, item.product_name, item.sku_description)}
                          >
                            เปลี่ยน
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Col>

          {/* ตรงกลาง: รายการจัดส่ง (Detail) */}
          <Col xs={24} md={24} lg={24} xl={8}>
            <HeaderTab>
              <Typography.Text style={{ fontWeight: "normal", paddingBottom: 5, paddingTop: 5 }}>
                รายละเอียดจัดส่ง (กล่อง/พัสดุ)
              </Typography.Text>
            </HeaderTab>
            <div style={{ borderRight: "1px solid #f0f0f0", height: "65vh", overflowY: "auto", padding: "8px" }}>
              {!selectedProductName ? (
                <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>กรุณาเลือกรายการสินค้าด้านซ้าย</div>
              ) : selectedSkuShipItems.length === 0 ? (
                <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>
                  {selectedSelfStatus === "SELF_NO_TRACKING"
                    ? "สินค้านี้จัดส่งเอง ยังไม่ได้บันทึกเลขพัสดุ"
                    : selectedSelfStatus === "SELF_SAVED"
                      ? "สินค้านี้จัดส่งเอง บันทึกเลขพัสดุแล้ว"
                      : "ไม่มีข้อมูลพัสดุสำหรับรายการนี้"}
                </div>
              ) : (
                selectedSkuShipItems.map((item, index) => {
                  const isFailed = item.status?.toUpperCase() === "FAILED" && !!item.carrier_code;
                  const isNoCarrier = item.status?.toUpperCase() === "FAILED" && !item.carrier_code;
                  const isPending = item.status?.toUpperCase() === "PENDING" || item.status?.toUpperCase() === "PROCESSING";
                  const isSuccess = item.status?.toUpperCase() === "SUCCESS";
                  const isItemSelected = selectedFailedItem?.verify_id === item.verify_id;

                  return (
                    <div
                      key={item.verify_id}
                      onClick={() => {
                        setSelfBrandId(undefined);
                        setSelfTrackingNumber('');
                        handleActionClick(item);
                      }}
                      style={{
                        padding: 12,
                        marginBottom: 10,
                        border: isItemSelected ? "1px solid #1890ff" : "1px solid #e8e8e8",
                        backgroundColor: isItemSelected ? "#e6f7ff" : "#fafafa",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                        <div style={{ flexShrink: 0 }}>
                          <img
                            src={item.product_image || "/placeholder-image.png"}
                            alt={item.product_name}
                            style={{
                              width: 80,
                              height: 80,
                              marginRight: 10,
                              objectFit: "cover",
                              borderRadius: 4
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, marginLeft: 10, display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2 }}>
                            <Typography.Paragraph style={{ marginBottom: 0, fontWeight: 500, fontSize: 14, lineHeight: 1.2, paddingRight: 8 }}>
                              {item.product_name}
                            </Typography.Paragraph>
                            <span style={{ fontSize: 14, color: "#999", flexShrink: 0 }}>
                              {index + 1}/{selectedSkuShipItems.length}
                            </span>
                          </div>
                          <Typography.Paragraph style={{ fontSize: "14px", color: "#888", marginBottom: 0 }}>
                            {item.sku_name}
                          </Typography.Paragraph>
                          <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ flex: 1 }}>
                              {isFailed && <Tag color="red" style={{ fontSize: 14 }}>❌ ขนส่งปฏิเสธ</Tag>}
                              {isNoCarrier && <Tag color="orange" style={{ whiteSpace: 'normal', height: 'auto', padding: '2px 6px', lineHeight: 1.4, fontSize: 14 }}>⚠️ SKU นี้ไม่ได้ตั้งค่าขนส่ง</Tag>}
                              {isPending && <Tag color="blue" style={{ fontSize: 14 }}>⏳ กำลังเรียกขนส่ง</Tag>}
                              {item.shipment_type === "self" && !item.self_tracking_number && (
                                <Tag color="orange" style={{ whiteSpace: 'normal', height: 'auto', padding: '2px 6px', lineHeight: 1.4, fontSize: 14 }}>⚠️ ยังไม่ได้บันทึกหมายเลขพัสดุ</Tag>
                              )}
                              
                              {(isSuccess || isPending) && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: (isPending || (item.shipment_type === "self" && !item.self_tracking_number)) ? 8 : 2 }}>
                                  <Space align="center">
                                    {item.carrier_logo ? (
                                      <img src={item.carrier_logo.replace('_SPEED','')} alt="logo" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                                    ) : (
                                      item.shipment_type === 'self' && <CarOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      {item.shipment_type === 'self' && item.self_tracking_number && (
                                        <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.2 }}>
                                          {item.self_tracking_number}
                                        </Typography.Text>
                                      )}
                                      <Typography.Text
                                        strong={!(item.shipment_type === 'self' && item.self_tracking_number)}
                                        style={{
                                          fontSize: 14,
                                          color: (item.shipment_type === 'self' && item.self_tracking_number) ? '#999' : '#000',
                                          lineHeight: 1.2,
                                          marginTop: (item.shipment_type === 'self' && item.self_tracking_number) ? 2 : 0
                                        }}
                                      >
                                        {(item.shipment_type === 'self' && item.self_tracking_number) ? "อ้างอิง: " : ""}
                                        {item.tracking_number || "รอขนส่งตอบรับ"}
                                      </Typography.Text>
                                    </div>
                                  </Space>
                                </div>
                              )}
                            </div>
                            {(isFailed || isNoCarrier) && (
                              <Button
                                size="small"
                                style={{
                                  fontSize: 14,
                                  backgroundColor: isItemSelected ? '#e49a05' : '#faad14',
                                  borderColor: '#d48806', 
                                  color: '#000', 
                                  fontWeight: 'normal',
                                  borderWidth: 1,
                                  borderStyle: 'solid'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionClick(item);
                                }}
                              >
                                เปลี่ยน
                              </Button>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Col>

          {/* ขวาสุด: การดำเนินการ (Action) */}
          <Col xs={24} md={24} lg={24} xl={8}>
            <HeaderTab>
              <Typography.Text style={{ fontWeight: "normal", paddingBottom: 5, paddingTop: 5 }}>
                การดำเนินการ
              </Typography.Text>
            </HeaderTab>
            <div style={{ borderRight: "1px solid #f0f0f0", height: "65vh", display: "flex", flexDirection: "column", padding: "8px" }}>
              {!selectedFailedItem && selectedOrderItem && selectedSelfStatus === "SELF_NO_TRACKING" ? (
                /* ===== SELF SHIPPING (ยังไม่มีแถวจัดส่ง): กรอกเลขพัสดุทั้งรายการ ===== */
                <SelfTrackingForm
                  key={selectedOrderItem.order_item_id}
                  rows={[
                    {
                      order_item_id: selectedOrderItem.order_item_id,
                      product_name: selectedOrderItem.product_name,
                      ship_qty: Number(selfItemsById[selectedOrderItem.order_item_id]?.item_qty) || Number(selectedOrderItem.sku_cnt) || 1,
                    },
                  ]}
                  brands={transportBrands}
                  onSavingChange={setSelfSaveLoading}
                  onSaved={handleSelfItemSaved}
                />
              ) : !selectedFailedItem ? (
                <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>
                  {selectedSelfStatus === "SELF_SAVED" ? "ไม่มีรายการที่ต้องดำเนินการ" : "ยังไม่ได้เลือกรายการใด ๆ"}
                </div>
              ) : selectedFailedItem.shipment_type === 'self' ? (
                /* ===== SELF SHIPPING FORM ===== */
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, backgroundColor: '#fff', marginBottom: 12 }}>
                    <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>พัสดุที่จัดส่งเอง</Typography.Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Typography.Text style={{ fontSize: 14, color: '#888' }}>เลขที่ออกโดยแบ่งเบา:</Typography.Text>
                      <Typography.Text style={{ fontSize: 14, color: '#1890ff', fontWeight: 500 }}>
                        {selectedFailedItem.tracking_number || '-'}
                      </Typography.Text>
                    </div>
                  </div>

                  {/* Select: บริษัทขนส่ง */}
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, backgroundColor: '#fff', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 8 }}>
                      <Typography.Text strong style={{ fontSize: 14 }}>บริษัทขนส่ง</Typography.Text>
                      <span style={{ color: '#ff4d4f', fontSize: 14, marginLeft: 2 }}>*</span>
                    </div>
                    <Select
                      placeholder="เลือกบริษัทขนส่ง"
                      style={{
                        width: '100%',
                        fontSize: 14,
                        borderColor: !selfBrandId ? '#ff4d4f' : undefined
                      }}
                      status={selfSubmitted && !selfBrandId ? 'error' : undefined}
                      value={selfBrandId}
                      ref={selectRef}
                      open={dropdownOpen}
                      onDropdownVisibleChange={(v) => setDropdownOpen(v)}
                      onChange={(v) => {
                        setSelfBrandId(v);
                        setDropdownOpen(false);
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                    >
                      {transportBrands.map((b) => (
                        <Select.Option key={b.uuid || b.id} value={b.uuid || b.id}>
                          <Space>
                            {b.brand_logo && <img src={b.brand_logo.replace('_SPEED','')} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />}
                            {b.brand_name}
                          </Space>
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  {/* Input: เลขพัสดุ หรือ เบอร์โทร */}
                  <div style={{ border: selfSubmitted && !selfTrackingNumber.trim() ? '1px solid #ff4d4f' : '1px solid #f0f0f0', borderRadius: 6, padding: 12, backgroundColor: '#fff', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 8 }}>
                      <Typography.Text strong style={{ fontSize: 14 }}>
                        {transportBrands.find((b) => (b.uuid || b.id) === selfBrandId)?.value === 'FT' ? 'หมายเลขโทรศัพท์' : 'เลขพัสดุ'}
                      </Typography.Text>
                      <span style={{ color: '#ff4d4f', fontSize: 14, marginLeft: 2 }}>*</span>
                    </div>
                    <Input
                      placeholder={transportBrands.find((b) => (b.uuid || b.id) === selfBrandId)?.value === 'FT' ? 'กรอกหมายเลขโทรศัพท์' : 'กรอกเลขพัสดุ'}
                      ref={inputRef}
                      value={selfTrackingNumber}
                      onChange={(e) => {
                        setSelfTrackingError(null);
                        const isSelectedFT = transportBrands.find((b) => (b.uuid || b.id) === selfBrandId)?.value === 'FT';
                        if (isSelectedFT) {
                          // อนุญาตเฉพาะตัวเลข สำหรับเบอร์โทร และตัดให้เหลือแค่ 10 หลัก
                          const cleaned = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setSelfTrackingNumber(cleaned);
                        } else {
                          // อนุญาตเฉพาะ a-z, A-Z, 0-9(กรองสัญลักษณ์พิเศษออกแบบ real-time)
                          const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                          setSelfTrackingNumber(cleaned);
                        }
                      }}
                      onPressEnter={handleSaveSelfTracking}
                      status={selfTrackingError ? 'error' : (selfSubmitted && !selfTrackingNumber.trim() ? 'error' : undefined)}
                      maxLength={transportBrands.find((b) => (b.uuid || b.id) === selfBrandId)?.value === 'FT' ? 10 : 30}
                      allowClear
                    />
                    {selfTrackingError && (
                      <Typography.Text type="danger" style={{ fontSize: 14, marginTop: 4, display: 'block' }}>
                        {selfTrackingError}
                      </Typography.Text>
                    )}
                  </div>

                  {/* ปุ่มบันทึก */}
                  <div style={{ marginTop: 'auto', paddingTop: 12 }}>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      loading={selfSaveLoading}
                      onClick={handleSaveSelfTracking}
                      style={{ width: '100%' }}
                    >
                      บันทึก
                    </Button>
                  </div>
                </div>
              ) : selectedFailedItem.status?.toUpperCase() !== 'FAILED' ? (
                /* ===== SUCCESS / PENDING STATE ===== */
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, backgroundColor: '#fff', marginBottom: 12 }}>
                    <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                      {selectedFailedItem.status?.toUpperCase() === 'SUCCESS' ? 'เรียกขนส่งสำเร็จ' : 'กำลังเรียกขนส่ง'}
                    </Typography.Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      {selectedFailedItem.carrier_logo && (
                        <img src={selectedFailedItem.carrier_logo.replace('_SPEED', '')} alt="logo" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                      )}
                      <Typography.Text style={{ fontSize: 14 }}>
                        {selectedFailedItem.carrier_name || selectedFailedItem.carrier_code || '-'}
                      </Typography.Text>
                    </div>
                    {selectedFailedItem.tracking_number && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography.Text style={{ fontSize: 14, color: '#888' }}>หมายเลขพัสดุ:</Typography.Text>
                        <Typography.Text style={{ fontSize: 14, color: '#1890ff', fontWeight: 500 }}>
                          {selectedFailedItem.tracking_number}
                        </Typography.Text>
                      </div>
                    )}
                    {selectedFailedItem.status?.toUpperCase() !== 'SUCCESS' && (
                      <div style={{ marginTop: 6 }}>
                        <Tag color="blue" style={{ fontSize: 14 }}>⏳ กำลังเรียกขนส่ง</Tag>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* ===== FAILED SHIPPING: CHANGE-CARRIER FORM ===== */
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  {/* ส่วนที่ 1: ขนส่งเดิม */}
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, backgroundColor: '#fff', marginBottom: 16 }}>
                     <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8, color: '#333'}}>ขนส่งเดิม</Typography.Text>
                     <Space align="center" style={{ marginTop: 4 }}>
                        {selectedFailedItem.carrier_logo && <img src={selectedFailedItem.carrier_logo} alt="logo" style={{ width: 36, height: 36, objectFit: 'contain' }} />}
                        <Typography.Text strong style={{ fontSize: 14 }}>{selectedFailedItem.carrier_name || selectedFailedItem.carrier_code}</Typography.Text>
                     </Space>
                     {selectedFailedItem.error_message && (
                     <div style={{ marginTop: 5 }}>
                         <Typography.Text type="danger" style={{ fontSize: 14 }}>
                           ❌ {selectedFailedItem.error_message}
                         </Typography.Text>
                     </div>
                     )}
                  </div>

                  {/* ส่วนที่ 2: เลือกขนส่งใหม่ */}
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, backgroundColor: '#fff', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                     <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>เลือกขนส่งใหม่</Typography.Text>
                     {carriersLoading[selectedFailedItem.verify_id] ? (
                       <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
                     ) : (alternativeCarriers[selectedFailedItem.verify_id] || []).length === 0 ? (
                       <div style={{ textAlign: "center", color: "#999", padding: 20 }}>ไม่พบข้อมูลขนส่ง</div>
                     ) : (
                       <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, overflowY: "auto", paddingRight: 4 }}>
                         {(alternativeCarriers[selectedFailedItem.verify_id] || []).map((c) => {
                           const isSelectedCarrier = selectedCarrier === c.carrier_code;
                           return (
                             <div 
                               key={c.carrier_code}
                               onClick={() => setSelectedCarrier(c.carrier_code)}
                               style={{ 
                                 display: "flex", 
                                 alignItems: "center", 
                                 justifyContent: "space-between", 
                                 border: isSelectedCarrier ? "1px solid #1890ff" : "1px solid #f0f0f0",
                                 backgroundColor: isSelectedCarrier ? "#e6f7ff" : "#fff",
                                 padding: "8px 12px",
                                 borderRadius: 6,
                                 cursor: "pointer"
                               }}
                             >
                                <Space>
                                  {c.logo && <img src={c.logo} alt="logo" style={{ width: 24, height: 24, objectFit: "contain" }} />}
                                  <span style={{ fontWeight: isSelectedCarrier ? 500 : 400, fontSize: 14 }}>{c.name}</span>
                                </Space>
                                <span style={{ fontSize: 14, fontWeight: 500 }}>{Math.round(Number(c.price))} บ.</span>
                             </div>
                           )
                         })}
                       </div>
                     )}
                  </div>

                  {/* ส่วนที่ 3: ปุ่มยืนยัน */}
                  <div style={{ paddingTop: 16, marginTop: 'auto' }}>
                     <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        loading={retryLoading}
                        onClick={handleRetry}
                        disabled={!selectedCarrier}
                        style={{ width: "100%", marginBottom: 12 }}
                     >
                        ยืนยันเปลี่ยนขนส่ง
                     </Button>
                     <Popconfirm
                        title="ต้องการเปลี่ยนเป็นจัดส่งเองใช่หรือไม่?"
                        onConfirm={handleSelfShip}
                        okText="ใช่"
                        cancelText="ไม่"
                     >
                        <Button
                          type="dashed"
                          danger
                          icon={<CodeSandboxOutlined />}
                          loading={retryLoading}
                          style={{ width: "100%" }}
                        >
                          เปลี่ยนเป็นส่งเอง
                        </Button>
                     </Popconfirm>
                  </div>
                </div>
              )}
            </div>
          </Col>

        </Row>
      )}
    </Modal>
  );
};

export default ShippingResultModal;

const HeaderTab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f0f0f0;
  border-top: 1px solid #f0f0f0;
`;
